// ** Modules
import { useState, useCallback } from 'react';

// ** UI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

// ** Components
import { CompetitionCard } from '@/components/common/CompetitionCard';
import { CompetitionDetailOverlay } from '@/components/common/CompetitionDetailOverlay';

// ** Types
import type { CompetitionGridBlockProps } from './CompetitionGrid.types';

const CompetitionGridBlock = ({ title, items, listLabel }: CompetitionGridBlockProps) => {
	const [overlayOpen, setOverlayOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);

	const openDetail = useCallback((index: number) => {
		setActiveIndex(index);
		setOverlayOpen(true);
	}, []);

	const closeDetail = useCallback(() => setOverlayOpen(false), []);

	const goPrev = useCallback(() => setActiveIndex((prev) => Math.max(0, prev - 1)), []);

	const goNext = useCallback(() => setActiveIndex((prev) => Math.min(items.length - 1, prev + 1)), [items.length]);

	if (!items.length) return null;

	return (
		<Box>
			{title && (
				<Box>
					<Typography variant="title-md" component="h2" sx={{ color: 'primary.main', lineHeight: '2rem' }}>
						{title}
					</Typography>
				</Box>
			)}

			<Box
				sx={{
					py: { xs: 4, md: 6 }
				}}
			>
				<Grid
					container
					spacing={4}
					component="ul"
					aria-label={listLabel ?? title}
					sx={{ listStyle: 'none', m: 0, p: 0 }}
				>
					{items.map((card, index) => (
						<Grid
							key={card.id}
							component="li"
							size={{ xs: 12, md: 6, lg: 4 }}
							sx={{ display: 'flex', justifyContent: 'center' }}
						>
							<CompetitionCard {...card} onClick={() => openDetail(index)} />
						</Grid>
					))}
				</Grid>
			</Box>

			<CompetitionDetailOverlay
				open={overlayOpen}
				items={items}
				activeIndex={activeIndex}
				onClose={closeDetail}
				onPrev={goPrev}
				onNext={goNext}
			/>
		</Box>
	);
};

export default CompetitionGridBlock;
